import React, { useCallback, useEffect, useState } from 'react'

import { useGetResourcePropertyUsageCountLazyQuery } from '~/client/graph/operations/generated/Resources.generated'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import TypeToConfirmDialog from '~/client/src/shared/components/TypeToConfirmDialog/TypeToConfirmDialog'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'

interface IProps {
  isOpen: boolean
  onClose: () => void
  propertyId: string
  projectId: string
  onComplete: () => void
  title: string
  confirmationWord: string
  getConfirmationText: (count: number) => string
  errorFallback: string
  useRedConfirmationButton?: boolean
  resourceSchema: ResourceSchema
  action: 'archive' | 'delete'
}

export default function PropertyActionDialog({
  isOpen,
  onClose,
  propertyId,
  projectId,
  onComplete,
  title,
  confirmationWord,
  getConfirmationText,
  errorFallback,
  useRedConfirmationButton: redButton,
  resourceSchema,
  action,
}: IProps) {
  const [fetchUsageCount, { data: countData, loading: countLoading }] =
    useGetResourcePropertyUsageCountLazyQuery({
      fetchPolicy: 'network-only',
    })
  const [error, setError] = useState<string | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [countFetchFailed, setCountFetchFailed] = useState(false)
  const [isExecuting, setIsExecuting] = useState(false)

  useEffect(() => {
    if (isOpen && propertyId && projectId) {
      setHasLoaded(false)
      setCountFetchFailed(false)
      setError(null)
      fetchUsageCount({
        variables: { projectId, id: propertyId },
      })
        .then(() => setHasLoaded(true))
        .catch(() => {
          setCountFetchFailed(true)
          setHasLoaded(true)
        })
    }
  }, [isOpen, propertyId, projectId, fetchUsageCount])

  const count = countData?.resourcePropertyUsageCount ?? 0
  const isLoading = countLoading || isExecuting

  const handleConfirm = useCallback(async () => {
    setIsExecuting(true)
    try {
      await (action === 'archive'
        ? resourceSchema.archiveProperty(propertyId)
        : resourceSchema.deleteProperty(propertyId))
      onComplete()
    } catch (err) {
      setError(err instanceof Error ? err.message : errorFallback)
    } finally {
      setIsExecuting(false)
    }
  }, [propertyId, onComplete, errorFallback, resourceSchema, action])

  if (!hasLoaded || countLoading) {
    return (
      <ConfirmDialog
        isOpen={isOpen}
        onCancelClicked={onClose}
        onDoneClicked={handleConfirm}
        title={title}
        question="..."
        doneTitle={title}
        loading={true}
        useRedConfirmationButton={redButton}
      />
    )
  }

  if (countFetchFailed) {
    return (
      <ConfirmDialog
        isOpen={isOpen}
        onCancelClicked={onClose}
        onDoneClicked={() => {
          setCountFetchFailed(false)
          setHasLoaded(false)
          fetchUsageCount({
            variables: { projectId, id: propertyId },
          })
            .then(() => setHasLoaded(true))
            .catch(() => {
              setCountFetchFailed(true)
              setHasLoaded(true)
            })
        }}
        title={title}
        question={Localization.translator.failedToLoadUsageData}
        doneTitle={Localization.translator.retry}
        useRedConfirmationButton={false}
      />
    )
  }

  if (count === 0) {
    return (
      <ConfirmDialog
        isOpen={isOpen}
        onCancelClicked={onClose}
        onDoneClicked={handleConfirm}
        title={title}
        question={getConfirmationText(0)}
        doneTitle={title}
        loading={isLoading}
        useRedConfirmationButton={redButton}
      >
        {error && (
          <p className="tw:text-md3-error tw:text-body-sm tw:mt-2">{error}</p>
        )}
      </ConfirmDialog>
    )
  }

  const description = (
    <>
      <span className="tw:font-medium">
        {Localization.translator.propertyUsedByXResources(count)}
      </span>{' '}
      {getConfirmationText(count)}
      {error && (
        <p className="tw:text-md3-error tw:text-body-sm tw:mt-2">{error}</p>
      )}
    </>
  )

  return (
    <TypeToConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleConfirm}
      title={title}
      description={description}
      confirmationValues={[String(count), confirmationWord]}
      confirmButtonLabel={title}
      loading={isLoading}
    />
  )
}
